/**
 * src/features/billing/lib/usePortal.ts
 *
 * POST-loading-error hook for the Stripe Customer Portal.
 * Calls POST /api/billing/portal, reads { url } from the response,
 * and redirects to the portal URL (same tab).
 *
 * Loading and error states are surfaced for accessible UI (aria-busy, role=alert).
 * T-08-18: the portal customer is derived server-side from the session — no client
 * value is sent.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface UsePortalReturn {
  loading: boolean;
  error: string | null;
  handlePortal: () => Promise<void>;
}

export function usePortal(): UsePortalReturn {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePortal = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/billing/portal', {
        method: 'POST',
      });
      if (!res.ok) {
        setError(t('billing.portal_error', 'Could not open subscription management. Please try again.'));
        return;
      }
      const { url } = (await res.json()) as { url: string };
      window.location.href = url;
    } catch {
      setError(t('billing.portal_error', 'Could not open subscription management. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return { loading, error, handlePortal };
}
